"use client";

import { useState } from "react";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { LogisticChart } from "@/components/tools/ecology/logistic-chart";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import type { NumberFormat } from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { niceTicks } from "@/lib/tool-kit/charts/scale";
import {
  chartHorizon,
  growthRateAt,
  inflectionOf,
  type LogisticParams,
  logisticOffset,
  logisticProblem,
  perCapitaRateAt,
  populationAt,
  reachRatio,
  timeToReach,
} from "@/lib/tool-kit/ecology/logistic";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const LOGISTIC_GROWTH_CALCULATOR_STRINGS = {
  n0Label: "Starting population N₀",
  kLabel: "Carrying capacity K",
  rLabel: "Intrinsic growth rate r (per time unit)",
  tLabel: "Time t",
  targetLabel: "Target population size (optional)",
  unitLabel: "Time unit (for labels)",
  unitDefault: "years",
  results: "Results",
  populationAt: "Population N(t) at t = {t} {unit}",
  shareOfK: "Share of carrying capacity",
  growthRate: "Growth rate dN/dt at that size (per time unit)",
  perCapita: "Per-capita rate r(1 − N ÷ K) (per time unit)",
  targetHeading: "Time to reach {target}",
  targetTime: "t = {time} {unit}",
  targetPast:
    "The model puts {target} at t = {time} {unit} — before your starting point, so the population is already larger than that at t = 0.",
  targetNever:
    "The curve never reaches {target}: it heads toward K = {k} without getting there, and it cannot cross K.",
  targetNeverBelow: "A target of zero or less cannot be reached.",
  inflectionHeading: "Inflection point",
  inflectionLine:
    "Growth is fastest when N = K ÷ 2 = {size}, at t = {time} {unit}, where dN/dt = rK ÷ 4 = {rate} per time unit.",
  inflectionPassed:
    "N₀ is already at or above K ÷ 2 = {size}, so growth only slows from t = 0. The steepest possible rate, rK ÷ 4, is {rate} per time unit.",
  workingHeading: "The working",
  workingOffset: "(K − N₀) ÷ N₀ = ({k} − {n0}) ÷ {n0} = {offset}",
  workingPopulation:
    "N(t) = K ÷ (1 + ((K − N₀) ÷ N₀) × e^(−rt)) = {k} ÷ (1 + {offset} × e^(−{r} × {t})) = {result}",
  workingRate: "dN/dt = rN(1 − N ÷ K) = {r} × {n} × (1 − {n} ÷ {k}) = {result}",
  workingTime:
    "t = ln(N(K − N₀) ÷ (N₀(K − N))) ÷ r = ln({ratio}) ÷ {r} = {result} {unit}",
  tableHeading: "Population over time",
  columnTime: "Time ({unit})",
  columnPopulation: "N(t)",
  columnRate: "dN/dt",
  columnShare: "% of K",
  chartHeading: "The S-shaped curve",
  chartTitle: "Logistic growth curve",
  chartY: "Population N",
  chartCapacity: "K = {k}",
  chartInflection: "Inflection, N = K ÷ 2",
  chartNow: "N(t)",
  chartDescription:
    "Logistic growth curve rising from {n0} at t = 0 toward the carrying capacity {k}, with growth fastest at N = {half}.",
  overshootNote:
    "N₀ is above K, so the population falls back toward K from above (dN/dt is negative).",
  assumptions:
    "The model assumes a closed population with a constant r and a fixed K, and no time lags. Real populations overshoot, oscillate and have K that changes — treat the curve as a baseline, not a forecast.",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy curve data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  enter: "Enter N₀, K, r and a time to see the result.",
  errN0: "Enter a starting population greater than 0.",
  errK: "Enter a carrying capacity greater than 0.",
  errR: "Enter a growth rate r greater than 0.",
  errT: "Enter a time of 0 or more.",
} as const;

type Strings = typeof LOGISTIC_GROWTH_CALCULATOR_STRINGS;

const ID = "lg";
const SIG = { maximumSignificantDigits: 5 } as const;

function problemText(
  problem: ReturnType<typeof logisticProblem>,
  strings: Strings
): string | null {
  if (problem === "n0") {
    return strings.errN0;
  }
  if (problem === "k") {
    return strings.errK;
  }
  return problem === "r" ? strings.errR : null;
}

export default function LogisticGrowthCalculatorWidget() {
  const strings = useToolUi(LOGISTIC_GROWTH_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [n0, setN0] = useState("50");
  const [k, setK] = useState("1000");
  const [r, setR] = useState("0.5");
  const [time, setTime] = useState("10");
  const [target, setTarget] = useState("750");
  const [unit, setUnit] = useState<string>(strings.unitDefault);

  const parsed = {
    n0: parseMeasuredNumber(n0),
    k: parseMeasuredNumber(k),
    r: parseMeasuredNumber(r),
  };
  const t = parseMeasuredNumber(time);
  const goal = target.trim() === "" ? null : parseMeasuredNumber(target);
  const params: LogisticParams | null =
    parsed.n0 === null || parsed.k === null || parsed.r === null
      ? null
      : { n0: parsed.n0, k: parsed.k, r: parsed.r };
  const problem = params ? logisticProblem(params) : null;
  const unitText = unit.trim() === "" ? strings.unitDefault : unit.trim();

  function body() {
    if (params === null) {
      return <Notice>{strings.enter}</Notice>;
    }
    const message = problemText(problem, strings);
    if (message) {
      return <ErrorNotice>{message}</ErrorNotice>;
    }
    if (t === null || t < 0) {
      return <ErrorNotice>{strings.errT}</ErrorNotice>;
    }
    return (
      <Results
        fmt={fmt}
        goal={goal}
        params={params}
        strings={strings}
        t={t}
        unit={unitText}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <TextField
          id={`${ID}-n0`}
          label={strings.n0Label}
          numeric
          onChange={setN0}
          value={n0}
        />
        <TextField
          id={`${ID}-k`}
          label={strings.kLabel}
          numeric
          onChange={setK}
          value={k}
        />
        <TextField
          id={`${ID}-r`}
          label={strings.rLabel}
          numeric
          onChange={setR}
          value={r}
        />
        <TextField
          id={`${ID}-t`}
          label={strings.tLabel}
          numeric
          onChange={setTime}
          value={time}
        />
        <TextField
          id={`${ID}-target`}
          label={strings.targetLabel}
          numeric
          onChange={setTarget}
          value={target}
        />
        <TextField
          id={`${ID}-unit`}
          label={strings.unitLabel}
          onChange={setUnit}
          value={unit}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body()}
      </section>
    </div>
  );
}

function Results({
  params,
  t: time,
  goal,
  unit,
  strings,
  fmt,
}: {
  params: LogisticParams;
  t: number;
  goal: number | null;
  unit: string;
  strings: Strings;
  fmt: NumberFormat;
}) {
  const n = populationAt(params, time);
  const rate = growthRateAt(params, n);
  const num = (value: number) => fmt(value, SIG);
  const inflection = inflectionOf(params);
  const offset = logisticOffset(params);
  const horizon = chartHorizon(params, time);
  const axis = niceTicks(0, horizon, { exact: true, count: 6 });
  const rows = axis.ticks.map((tick) => {
    const size = populationAt(params, tick);
    return [
      fmt(tick, SIG),
      num(size),
      num(growthRateAt(params, size)),
      fmt((size / params.k) * 100, { maximumFractionDigits: 1 }),
    ];
  });
  const head = [
    t(strings.columnTime, { unit }),
    strings.columnPopulation,
    strings.columnRate,
    strings.columnShare,
  ];
  const xEnd = axis.ticks.at(-1) ?? horizon;
  const curveRows = Array.from({ length: 41 }, (_, i) => {
    const tick = (xEnd * i) / 40;
    return [tick.toFixed(4), populationAt(params, tick).toFixed(4)];
  });
  const base = {
    r: num(params.r),
    k: num(params.k),
    n0: num(params.n0),
  };
  return (
    <div className="space-y-6">
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.populationAt, { t: num(time), unit })}
          value={num(n)}
        />
        <StatCard
          label={strings.shareOfK}
          value={`${fmt((n / params.k) * 100, { maximumFractionDigits: 1 })}%`}
        />
        <StatCard label={strings.growthRate} value={num(rate)} />
        <StatCard
          label={strings.perCapita}
          value={num(perCapitaRateAt(params, n))}
        />
      </StatGrid>
      {params.n0 > params.k ? <Notice>{strings.overshootNote}</Notice> : null}
      {goal === null ? null : (
        <TargetCard
          fmt={fmt}
          goal={goal}
          params={params}
          strings={strings}
          unit={unit}
        />
      )}
      <div className={`${CARD} space-y-1`}>
        <h3 className={WIDGET_H3}>{strings.inflectionHeading}</h3>
        <p className="text-foreground text-sm">
          {inflection.time === null
            ? t(strings.inflectionPassed, {
                size: num(inflection.size),
                rate: num(inflection.maxRate),
                unit,
              })
            : t(strings.inflectionLine, {
                size: num(inflection.size),
                time: num(inflection.time),
                rate: num(inflection.maxRate),
                unit,
              })}
        </p>
      </div>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <ul className="space-y-1 font-mono text-foreground text-sm">
          <li>{t(strings.workingOffset, { ...base, offset: num(offset) })}</li>
          <li>
            {t(strings.workingPopulation, {
              ...base,
              offset: num(offset),
              t: num(time),
              result: num(n),
            })}
          </li>
          <li>
            {t(strings.workingRate, {
              r: base.r,
              n: num(n),
              k: base.k,
              result: num(rate),
            })}
          </li>
        </ul>
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
        <ChartFigure
          apa={false}
          data={toTsv([
            [t(strings.columnTime, { unit }), strings.columnPopulation],
            ...curveRows,
          ])}
          figureNumber="1"
          filename={chartFilename(strings.chartTitle, "logistic-growth")}
          labels={{
            ...strings,
            figureLabel: "",
            noteLabel: "",
            copyCaption: "",
          }}
          note=""
          table={null}
          title={strings.chartTitle}
        >
          <LogisticChart
            fmt={fmt}
            id={`${ID}-chart`}
            inflection={inflection}
            labels={{
              xTitle: t(strings.columnTime, { unit }),
              yTitle: strings.chartY,
              capacity: t(strings.chartCapacity, { k: base.k }),
              inflection: strings.chartInflection,
              current: strings.chartNow,
              title: strings.chartTitle,
              description: t(strings.chartDescription, {
                n0: base.n0,
                k: base.k,
                half: num(inflection.size),
              }),
            }}
            now={time <= xEnd ? { t: time, n } : null}
            params={params}
            xMax={xEnd}
          />
        </ChartFigure>
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <DataTable head={head} rows={rows} />
      </div>
      <p className={HINT}>{strings.assumptions}</p>
    </div>
  );
}

function TargetCard({
  params,
  goal,
  unit,
  strings,
  fmt,
}: {
  params: LogisticParams;
  goal: number;
  unit: string;
  strings: Strings;
  fmt: NumberFormat;
}) {
  const num = (value: number) => fmt(value, SIG);
  const reach = timeToReach(params, goal);
  const heading = t(strings.targetHeading, { target: num(goal) });
  let line: string;
  if (goal <= 0) {
    line = strings.targetNeverBelow;
  } else if (reach === null) {
    line = t(strings.targetNever, { target: num(goal), k: num(params.k) });
  } else if (reach < 0) {
    line = t(strings.targetPast, {
      target: num(goal),
      time: num(reach),
      unit,
    });
  } else {
    line = t(strings.targetTime, { time: num(reach), unit });
  }
  return (
    <div className={`${RESULT_CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{heading}</h3>
      <p className="text-foreground text-sm">{line}</p>
      {reach === null ? null : (
        <p className="font-mono text-foreground text-sm">
          {t(strings.workingTime, {
            ratio: num(reachRatio(params, goal)),
            r: num(params.r),
            result: num(reach),
            unit,
          })}
        </p>
      )}
    </div>
  );
}
